import { isValidANumber, isValidPhoneNumber } from "@/utils/gphonenumberlib";

export class ModelValidation {
    public aNumbersNoteMaxLength = 256;
    public contentDescriptionMaxLength = 256;
    public contentNameMaxLenth = 256;
    public demoCustomTextMaxLength = 20;

    public get fieldRequiredValidationRules(): Array<(value: string) => boolean | string> {
        return [(x) => !!x || "Field is required"];
    }

    public get ttlValidationRules(): Array<
        (value: string) => boolean | string
    > {
        return [
            (x) => {
                if (!x) {
                    return "TTL minutes is required";
                } else if (Number(x) < 16) {
                    return `Minimum TTL is 16 Minutes`;
                } else if (Number(x) > 172800) {
                    return `Maximum TTL is 172,800 Minutes`;
                }
                return true;
            },
        ];
    }

    public get businessNameValidationRules(): Array<(value: string) => boolean | string> {
        return [(x) => !!x || "Business name is required"];
    }

    public get nameValidationRules(): Array<(value: string) => boolean | string> {
        return [(x) => !!x || "A name is required"];
    }

    public get contentNameValidationRules(): Array<
        (value: string) => boolean | string
    > {
        return [
            (x) => {
                if (!x) {
                    return "Name is required";
                } else if (x.length > this.contentNameMaxLenth) {
                    return `Cannot exceed ${this.contentNameMaxLenth} Characters`;
                }
                return true;
            },
        ];
    }

    public get imageValidationRules(): Array<
        (value: string) => boolean | string
    > {
        return [(x) => !!x || "An image is required"];
    }

    public get roleValidationRules(): Array<
        (value: string) => boolean | string
    > {
        return [(x) => !!x || "Role selection is required"];
    }

    public get descriptionValidationRules(): Array<
        (value: string) => boolean | string
    > {
        return [(x) => !!x || "Description is required"];
    }

    public get contentDescriptionValidationRules(): Array<
        (value: string) => boolean | string
    > {
        return [
            (x) => {
                if (!x) {
                    return "Description is required";
                } else if (x.length > this.contentDescriptionMaxLength) {
                    return `Cannot exceed ${this.contentDescriptionMaxLength} Characters`;
                }
                return true;
            },
        ];
    }

    public get descriptionLengthValidationRules(): Array<(value: string) => boolean | string> {
        return [
            (x) => {
                if (!x) {
                    return "Description is required";
                } else if (x.length > 32) {
                    return "This description can have a max of 32 characters.";
                }
                return true;
            },
        ];
    }

    public get contactEmailValidationRules(): Array<(value: string) => boolean | string> {
        const regex = /^[a-zA-Z0-9]+(?:[_.-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)*(?:\.[a-zA-Z]{2,7})$/;

        return [
            (x) => {
                if (!x || x.length === 0) {
                    return "Email address is required.";
                }

                if (!regex.test(x)) {
                    return "Must be in the format of a valid email address";
                }

                return true;
            },
        ];
    }

    public get phoneNumberValidationRules(): Array<(value: string) => boolean | string> {
        return [
            (x) => {
                if (!x) {
                    return "Phone number is required";
                }
                else if (!isValidPhoneNumber(x)) {
                    return "Phone number must be valid and in e164 format.";
                }
                return true;
            },
        ];
    }

    public get aNumberValidationRules(): Array<(value: string) => boolean | string> {
        return [
            (x) => {
                if (!x) {
                    return "ANumber is required";
                }
                else if (!isValidANumber(x)) {
                    return "ANumber must be valid in numeric format.";
                }
                return true;
            },
        ];
    }

    public get noteValidationRules(): Array<
        (value: string) => boolean | string
    > {
        return [
            (x) => {
                if (!x) {
                    return "Note is required";
                }

                if (x.length > this.aNumbersNoteMaxLength) {
                    return `Note cannot exceed ${this.aNumbersNoteMaxLength} characters`;
                }

                return true;
            },
        ];
    }

    public get cnamValidationRules(): Array<
        (value: string) => boolean | string
    > {
        const self = this;
        const regex = /^[a-zA-Z0-9 &.-]*$/
        return [
            (x) => {
                if (!x || x.length === 0) {
                    return "CNAM is required.";
                }

                if (x.length > 32) {
                    return "CNAM has a max length of 32.";
                }

                if (!regex.test(x)) {
                    return "CNAM includes illegal characters. Only alphanumeric, '.', '&', '-', and ' ' allowed.";
                }

                return true;
            },
        ];
    }

    public get baselineAnswerRateRules(): Array<(value: string) => boolean | string> {
        const self = this;
        const regex = /^\d+(?:\d{1,2})?$/;
        return [
            (x) => {
                if (!x || x.length === 0) {
                    return "Baseline answer rate is required.";
                }

                if (x.length > 2) {
                    return "Baseline answer rate must be a whole number between 0 to 99";
                }

                if (!regex.test(x)) {
                    return "Baseline answer rate must be a whole number between 0 to 99";
                }

                return true;
            },
        ];
    }

    public get demoCustomTextValidationRules(): Array<(value: string) => boolean | string> {
        return [
            (x) =>
                x.length < 1 ||
                `Messages over 20 characters may not show correctly on all devices`,
        ];
    }

    public get pushNotificationRules(): Array<(value: string) => boolean | string> {
        return [
            (x) => {
                if (!x || x.length === 0) {
                    return "Pre-Call Notification required.";
                }

                return true;
            }
        ];
    }

    public get cleanNotificationRules(): Array<(value: string) => boolean | string> {
        return [
            (x) => {
                if (!x || x.length === 0) {
                    return "Post-Call Notification required.";
                }

                return true;
            }
        ];
    }
}
